<section *ngIf="!retainedIssue; else unsupportedConfiguration" class="status-editor" [attr.aria-labelledby]="headingId">
<ng-container *ngIf="compactWorkspace; else legacyEditor">
  <header>
    <div><h4 [id]="headingId" data-status-heading tabindex="-1">{{ title }}</h4>
      <p *ngIf="nodeId === null; else visitLabelMeaning">Shared across every step of {{ scopeLabel }}. These business labels do not rename progress, record a decision or route work.</p>
      <ng-template #visitLabelMeaning><p>Local to a visit to {{ scopeLabel }}. These labels do not complete the step, satisfy requirements or route work. Standard progress and shared workflow statuses remain separate.</p></ng-template></div>
  </header>
  <p *ngIf="error" role="alert" tabindex="-1">{{ error }}</p><p *ngIf="message" role="status">{{ message }}</p>
  <section class="status-workflow-behavior" [attr.aria-labelledby]="behaviorHeadingId">
    <h5 [id]="behaviorHeadingId">{{ nodeId === null ? 'Workflow behavior' : 'Step label behavior' }}</h5>
    <ng-container *ngIf="config.statuses.length && settingsEditorView as editor; else emptyBehavior">
      <ui-form [instanceId]="formInstanceId('settings')" [config]="editor.config" [formValues]="settingsValues"
        [disabled]="disabled || nameEditing || ruleEditing" (formValuesChange)="onSettingsChange($event, editor.origin)" />
    </ng-container>
    <ng-template #emptyBehavior><p>{{ nodeId === null ? 'Add a status before choosing a starting status or allowing manual changes.' : 'Add a step label before choosing a starting label or allowing manual changes.' }}</p></ng-template>
    <p>{{ nodeId === null ? 'Manual changes still require staff authority and current work. Automatic events can change the label later.' : 'Manual step-label changes still require staff authority and current work. Automatic events update only this visit’s label.' }}</p>
  </section>
  <ui-tab-group [navigationTabs]="workspaceTabs" [activeTabId]="workspaceView" [stretchTabs]="false" [navigationAriaLabel]="nodeId === null ? 'Workflow status views' : 'Step label views'" (tabRequested)="selectWorkspaceView($event)">
    <div class="status-workspace" *ngIf="workspaceView === 'labels'">
      <ui-panel [panelTitle]="nodeId === null ? 'Status labels' : 'Step labels'" [noGrow]="true" class="status-workspace-list">
        <button uiButton type="button" *ngIf="canAuthor && !(nameEditing && !editingCode)" [disabled]="disabled || nameEditing || ruleEditing" (click)="addStatus()">{{ nodeId === null ? 'Add status' : 'Add step label' }}</button>
        <p *ngIf="!config.statuses.length && !nameEditing">{{ nodeId === null ? 'No workflow status labels are configured. Add only the labels this workflow needs.' : 'No optional step labels are configured. Add only the labels this visit needs.' }}</p>
        <nav [attr.aria-label]="nodeId === null ? 'Workflow status labels' : 'Optional step labels'" class="status-workspace-rows">
          <button uiButton [text]="true" [wrap]="true" type="button" *ngFor="let status of config.statuses; trackBy: trackStatus"
            [attr.data-status-label]="status.code" [attr.aria-pressed]="selectedStatus?.code === status.code"
            [disabled]="interactionBlocked || nameEditing || ruleEditing" (click)="selectStatus(status.code)">
            <span><strong>{{ status.label || 'Unnamed status' }}</strong><small *ngIf="status.code === config.initialStatusCode">{{ nodeId === null ? 'Starting status' : 'Starting step label' }}</small></span>
          </button>
        </nav>
      </ui-panel>
      <ui-panel *ngIf="nameEditing; else savedLabelDetail" [panelTitle]="nodeId === null ? (editingCode ? 'Rename status' : 'New status') : (editingCode ? 'Rename step label' : 'New step label')" [noGrow]="true" class="status-workspace-detail">
        <section data-status-name-editor aria-label="Status name editor" class="status-workspace-body">
          <ng-container *ngIf="nameEditorView as editor"><ui-form [instanceId]="formInstanceId('name')" [config]="editor.config" [formValues]="nameValues" [disabled]="disabled" (formValuesChange)="onNameChange($event, editor.origin)" (keydown)="onNameKeydown($event, editor.origin)" (focusout)="onNameFocusout($event, editor.origin)" /></ng-container>
          <div class="status-workspace-actions"><button uiButton type="button" [disabled]="disabled" (click)="saveName()">{{ editingCode ? 'Save name' : nodeId === null ? 'Add status' : 'Add step label' }}</button>
            <button uiButton [text]="true" type="button" [disabled]="interactionBlocked" (mousedown)="$event.preventDefault()" (click)="cancelName()">Cancel</button></div>
        </section>
      </ui-panel>
      <ng-template #savedLabelDetail>
        <ui-panel *ngIf="selectedStatus as status; else noLabelSelection" [panelTitle]="status.label || 'Unnamed status'" [noGrow]="true" class="status-workspace-detail" [attr.data-status-label-detail]="status.code">
          <div class="status-workspace-body"><p>{{ status.label || 'Unnamed status' }}</p>
            <div class="status-workspace-actions" *ngIf="canAuthor"><button uiButton type="button" [attr.aria-label]="'Rename ' + status.label" [disabled]="disabled || nameEditing || ruleEditing" (click)="editStatus(status.code)">Rename</button>
              <button uiButton severity="danger" type="button" [disabled]="disabled || nameEditing || ruleEditing" (click)="removeStatus(status.code)">{{ nodeId === null ? 'Remove status' : 'Remove step label' }}</button></div>
            <p *ngIf="statusRemovalMessage" data-status-removal-refusal role="status" tabindex="-1">{{ statusRemovalMessage }}</p>
            <h5>{{ nodeId === null ? 'Usage in this configuration' : 'Usage in this step configuration' }}</h5><p *ngIf="!selectedStatusRules.length">No automatic change currently targets this label.</p>
            <ul *ngIf="selectedStatusUsage as usage"><li *ngFor="let item of usage; trackBy: trackStatusUsage" class="status-workspace-usage" [attr.data-status-usage-row]="item.rule.id">
              <span>{{ item.summary }}</span>
              <button uiButton [text]="true" [wrap]="true" type="button" [attr.data-status-usage-rule]="item.rule.id"
                [attr.aria-label]="'Review automatic change: ' + item.summary" [disabled]="interactionBlocked || nameEditing || ruleEditing"
                (click)="reviewAutomaticChange(item.navigation)">Review automatic change</button>
            </li></ul>
            <p *ngIf="status.code === config.initialStatusCode">{{ nodeId === null ? 'This is the workflow’s starting status. Choose another start before removing it while other labels remain.' : 'This is this visit’s starting step label. Choose another start before removing it while other labels remain.' }}</p>
            <details><summary>Technical identity</summary><code>{{ status.code }}</code><p>Renaming keeps this generated identity and its existing rule references.</p></details>
          </div>
        </ui-panel>
        <ng-template #noLabelSelection><p class="status-workspace-empty">{{ config.statuses.length ? (nodeId === null ? 'The selected status is unavailable. Choose a current label to inspect it.' : 'The selected step label is unavailable. Choose a current label to inspect it.') : (nodeId === null ? 'No status label selected.' : 'No step label selected.') }}</p></ng-template>
      </ng-template>
    </div>
    <div class="status-workspace" *ngIf="workspaceView === 'changes'">
      <ui-panel panelTitle="Automatic changes" [noGrow]="true" class="status-workspace-list">
        <ng-container *ngIf="automaticChangeReturnView as back">
          <button uiButton [text]="true" [wrap]="true" type="button" data-status-label-return
            [disabled]="interactionBlocked || nameEditing || ruleEditing || !back.available" (click)="backToStatusLabel(back.navigation)">{{ back.label }}</button>
          <p *ngIf="!back.available" role="status">{{ nodeId === null ? 'The original status label is unavailable. Choose a current label in Status labels.' : 'The original step label is unavailable. Choose a current label in Step labels.' }}</p>
        </ng-container>
        <button uiButton type="button" *ngIf="canAuthor" [disabled]="disabled || !config.statuses.length || nameEditing || ruleEditing" (click)="addRule()">Add automatic change</button>
        <p *ngIf="!config.rules.length">No automatic changes are configured.</p>
        <nav [attr.aria-label]="nodeId === null ? 'Automatic workflow status changes' : 'Automatic step label changes'" class="status-workspace-rows">
          <button uiButton [text]="true" [wrap]="true" type="button" *ngFor="let rule of config.rules; trackBy: trackRule" [attr.data-status-rule]="rule.id"
            [attr.aria-pressed]="selectedRule?.id === rule.id" [disabled]="interactionBlocked || nameEditing || ruleEditing" (click)="selectRule(rule.id)"><span>{{ ruleSummary(rule) }}</span></button>
        </nav>
        <details><summary>How automatic changes work</summary><p *ngIf="nodeId === null; else visitRuleMeaning">Events are processed in time and sequence. The last matching rule for an event wins. A followed-outcome mapping takes precedence for that advancement; process completion can set the final label afterward. Rule order does not change graph execution order.</p>
          <ng-template #visitRuleMeaning><p>Events for this step visit are processed in time and sequence. The last matching rule for an event wins. A form-save label requires matching evidence for the exact requirement and visit; required work and step completion stay independent.</p></ng-template></details>
      </ui-panel>
      <ui-panel *ngIf="ruleEditing; else savedRuleDetail" [panelTitle]="editingRuleId ? 'Edit automatic change' : 'New automatic change'" [noGrow]="true" class="status-workspace-detail">
        <section data-status-rule-editor aria-label="Automatic status change editor" class="status-workspace-body">
          <ng-container *ngIf="ruleEditorView as editor"><ui-form [instanceId]="formInstanceId('rule')" [config]="editor.config" [formValues]="ruleValues" [disabled]="disabled" (formValuesChange)="onRuleChange($event, editor.origin)" /></ng-container>
          <div class="status-workspace-actions"><button uiButton type="button" [disabled]="disabled" (click)="saveRule()">Save automatic change</button><button uiButton [text]="true" type="button" [disabled]="interactionBlocked" (click)="cancelRule()">Cancel</button></div>
        </section>
      </ui-panel>
      <ng-template #savedRuleDetail>
        <ui-panel *ngIf="selectedRule as rule; else noRuleSelection" panelTitle="Automatic change" [noGrow]="true" class="status-workspace-detail" [attr.data-status-rule-detail]="rule.id">
          <div class="status-workspace-body"><p>{{ ruleSummary(rule) }}</p>
            <dl><div><dt>When</dt><dd>{{ eventLabel(rule.eventCode) }}</dd></div><div *ngIf="nodeId === null && rule.sourceNodeId"><dt>In step</dt><dd>{{ sourceLabel(rule.sourceNodeId) }}</dd></div><div><dt>{{ nodeId === null ? 'Set workflow status to' : 'Set step label to' }}</dt><dd>{{ statusLabel(rule.statusCode) }}</dd></div></dl>
            <p *ngIf="ruleIssue(rule)" role="status">{{ ruleIssue(rule) }} The saved rule is retained until explicitly repaired or removed.</p>
            <div class="status-workspace-actions" *ngIf="canAuthor"><button uiButton type="button" [disabled]="disabled || nameEditing || ruleEditing" (click)="editRule(rule.id)">Edit automatic change</button>
              <details><summary>Rule actions</summary><div class="status-workspace-actions"><button uiButton [text]="true" type="button" [disabled]="disabled || config.rules[0]?.id === rule.id" (click)="moveRule(rule.id, -1)">Move earlier</button><button uiButton [text]="true" type="button" [disabled]="disabled || config.rules[config.rules.length - 1]?.id === rule.id" (click)="moveRule(rule.id, 1)">Move later</button><button uiButton severity="danger" type="button" [disabled]="disabled" (click)="removeRule(rule.id)">Remove automatic change</button></div></details>
            </div>
            <details><summary>Technical identity</summary><code>{{ rule.id }}</code></details>
          </div>
        </ui-panel>
        <ng-template #noRuleSelection><p class="status-workspace-empty">{{ config.rules.length ? 'The selected automatic change is unavailable. Choose a current rule to inspect it.' : 'No automatic change selected.' }}</p></ng-template>
      </ng-template>
    </div>
  </ui-tab-group>
  <footer *ngIf="pending && !nameEditing && !ruleEditing" class="status-workspace-actions"><button uiButton type="button" [disabled]="disabled" (click)="applyVisibleChanges()">Retry changes</button><button uiButton [text]="true" type="button" [disabled]="interactionBlocked" (click)="discardVisibleChanges()">{{ nodeId === null ? 'Cancel status changes' : 'Cancel step label changes' }}</button></footer>
</ng-container>
<ng-template #legacyEditor>
  <ng-template #statusHeader
    ><header>
      <div>
        <h4 [id]="headingId" data-status-heading tabindex="-1">{{ title }}</h4>
        <p>
          {{
            nodeId === null
              ? "Shared across every step of "
              : "Local detail for a visit to "
          }}{{ scopeLabel }}.
        </p>
      </div>
      <ui-button
        *ngIf="canAuthor && !(immediateChanges && nameEditing && !editingCode)"
        severity="primary"
        [disabled]="disabled || nameEditing || ruleEditing"
        (click)="addStatus()"
        >{{ nodeId === null ? "Add status" : "Add step label" }}</ui-button
      >
    </header></ng-template
  >
  <ui-panel [headerTemplate]="statusHeader" [noGrow]="true"
    ><div class="status-editor__content">
      <details class="status-editor__help">
        <summary>
          About {{ nodeId === null ? "workflow statuses" : "step labels" }}
        </summary>
        <p *ngIf="nodeId === null">
          Define business states such as Awaiting documents, Awaiting review or
          Returned. A workflow keeps this status when it moves to another step,
          until an action, event or permitted manual change updates it. Step
          progress is tracked separately.
        </p>
        <p *ngIf="nodeId !== null">
          These optional labels describe only this step visit, alongside its
          standard progress. Use workflow statuses for shared states such as
          Returned or Awaiting documents that must remain visible after work
          moves to another step.
        </p>
      </details>
      <p
        *ngIf="!config.statuses.length && !nameEditing"
        class="status-editor__empty"
      >
        No custom statuses yet.
      </p>
      <p *ngIf="error" role="alert" tabindex="-1">{{ error }}</p>
      <p *ngIf="message" role="status">{{ message }}</p>
      <ol class="status-editor__statuses">
        <li
          *ngFor="let status of config.statuses"
          [attr.data-status-code]="status.code"
        >
          <span
            *ngIf="
              immediateChanges && nameEditing && editingCode === status.code;
              else statusName
            "
            class="status-editor__inline-name"
            (focusout)="onNameFocusout($event)"
            (keydown.escape)="onNameKeydown($event)"
          >
            <input
              class="status-editor__name-input"
              type="text"
              [attr.aria-label]="
                nodeId === null ? 'Workflow status name' : 'Step label name'
              "
              [value]="nameValues.label"
              [disabled]="disabled"
              maxlength="120"
              (input)="onNameChange({ label: $any($event.target).value })"
              (keydown)="onNameKeydown($event)"
            />
            <button
              type="button"
              class="status-editor__cancel-name"
              [disabled]="interactionBlocked"
              (mousedown)="$event.preventDefault()"
              (click)="cancelName()"
            >
              Cancel rename
            </button>
          </span>
          <ng-template #statusName>
            <button
              *ngIf="immediateChanges && canAuthor; else readOnlyStatusName"
              type="button"
              class="status-editor__name"
              [disabled]="disabled || nameEditing || ruleEditing"
              [attr.aria-label]="'Rename ' + status.label"
              (click)="editStatus(status.code)"
            >
              {{ status.label }}
            </button>
            <ng-template #readOnlyStatusName
              ><strong>{{ status.label }}</strong></ng-template
            >
          </ng-template>
          <span *ngIf="status.code === config.initialStatusCode">{{
            immediateChanges
              ? "Starting"
              : nodeId === null
                ? "Starting workflow status"
                : "Starting step label"
          }}</span>
          <div
            *ngIf="
              canAuthor &&
              !(immediateChanges && nameEditing && editingCode === status.code)
            "
          >
            <ui-button
              *ngIf="!immediateChanges"
              severity="secondary"
              [disabled]="disabled || nameEditing || ruleEditing"
              (click)="editStatus(status.code)"
              >Rename</ui-button
            >
            <ui-button
              severity="danger"
              [disabled]="disabled || nameEditing || ruleEditing"
              (click)="removeStatus(status.code)"
              >Remove</ui-button
            >
          </div>
        </li>
      </ol>
      <section
        *ngIf="nameEditing && (!immediateChanges || !editingCode)"
        class="status-editor__edit"
        aria-label="Status name editor"
      >
        <h5>{{ editingCode ? "Rename status" : "New status" }}</h5>
        <input
          *ngIf="immediateChanges; else sharedNameForm"
          class="status-editor__name-input"
          type="text"
          [attr.aria-label]="
            nodeId === null ? 'Workflow status name' : 'Step label name'
          "
          [value]="nameValues.label"
          [disabled]="disabled"
          maxlength="120"
          placeholder="For example: Awaiting documents"
          (input)="onNameChange({ label: $any($event.target).value })"
          (keydown)="onNameKeydown($event)"
        />
        <ng-template #sharedNameForm
          ><ui-form
            [instanceId]="formInstanceId('name')"
            [config]="nameConfig"
            [formValues]="nameValues"
            [disabled]="disabled"
            (formValuesChange)="onNameChange($event)"
        /></ng-template>
        <ui-button
          severity="primary"
          [disabled]="disabled"
          (click)="saveName()"
          >{{
            immediateChanges
              ? "Add status"
              : editingCode
                ? "Use this name"
                : "Add to statuses"
          }}</ui-button
        >
        <ui-button
          *ngIf="immediateChanges"
          severity="secondary"
          [disabled]="interactionBlocked"
          (click)="cancelName()"
          >Cancel</ui-button
        >
      </section>
      <ui-form
        *ngIf="config.statuses.length"
        [instanceId]="formInstanceId('settings')"
        [config]="settingsConfig"
        [formValues]="settingsValues"
        [disabled]="disabled || nameEditing || ruleEditing"
        (formValuesChange)="onSettingsChange($event)"
      />
      <section
        *ngIf="config.statuses.length"
        [attr.aria-labelledby]="eventsHeadingId"
      >
        <header>
          <div>
            <h5 [id]="eventsHeadingId">
              {{
                nodeId === null
                  ? "Automatic workflow status changes"
                  : "Automatic step label changes"
              }}
            </h5>
          </div>
          <ui-button
            *ngIf="canAuthor"
            severity="secondary"
            [disabled]="disabled || nameEditing || ruleEditing"
            (click)="addRule()"
            >Add automatic change</ui-button
          >
        </header>
        <p *ngIf="!config.rules.length">No automatic changes yet.</p>
        <details class="status-editor__help">
          <summary>How automatic changes work</summary>
          <p>
            The latest matching event sets
            {{ nodeId === null ? "the workflow status" : "this step label" }}.
            If several rules match that event, the last rule wins.
          </p>
        </details>
        <ol class="status-editor__rules">
          <li *ngFor="let rule of config.rules; let index = index">
            <strong>{{ ruleSummary(rule) }}</strong>
            <div *ngIf="canAuthor">
              <ui-button
                severity="secondary"
                [disabled]="disabled || nameEditing || ruleEditing"
                (click)="editRule(rule.id)"
                >Edit</ui-button
              >
              <ui-button
                severity="secondary"
                [disabled]="
                  disabled || nameEditing || ruleEditing || index === 0
                "
                (click)="moveRule(rule.id, -1)"
                [ariaLabel]="'Move rule ' + (index + 1) + ' earlier'"
                >Earlier</ui-button
              >
              <ui-button
                severity="secondary"
                [disabled]="
                  disabled ||
                  nameEditing ||
                  ruleEditing ||
                  index === config.rules.length - 1
                "
                (click)="moveRule(rule.id, 1)"
                [ariaLabel]="'Move rule ' + (index + 1) + ' later'"
                >Later</ui-button
              >
              <ui-button
                severity="danger"
                [disabled]="disabled || nameEditing || ruleEditing"
                (click)="removeRule(rule.id)"
                >Remove</ui-button
              >
            </div>
          </li>
        </ol>
        <section
          *ngIf="ruleEditing"
          class="status-editor__edit"
          aria-label="Automatic status change editor"
        >
          <h5>
            {{
              editingRuleId ? "Edit automatic change" : "New automatic change"
            }}
          </h5>
          <ui-form
            [instanceId]="formInstanceId('rule')"
            [config]="ruleConfig"
            [formValues]="ruleValues"
            [disabled]="disabled"
            (formValuesChange)="onRuleChange($event)"
          />
          <ui-button
            severity="primary"
            [disabled]="disabled"
            (click)="saveRule()"
            >{{
              immediateChanges ? "Save automatic change" : "Use this rule"
            }}</ui-button
          >
          <ui-button
            *ngIf="immediateChanges"
            severity="secondary"
            [disabled]="interactionBlocked"
            (click)="cancelRule()"
            >Cancel</ui-button
          >
        </section>
      </section>
      <footer
        *ngIf="pending && (!immediateChanges || (!nameEditing && !ruleEditing))"
      >
        <p *ngIf="!immediateChanges">
          These changes are pending. Apply them here or use Save draft. Cancel
          restores the saved status settings for this scope.
        </p>
        <ui-button
          severity="primary"
          [disabled]="disabled"
          (click)="applyPendingChanges()"
          >{{
            immediateChanges
              ? "Retry changes"
              : nodeId === null
                ? "Apply statuses"
                : "Apply step labels"
          }}</ui-button
        >
        <ui-button
          severity="secondary"
          [disabled]="interactionBlocked"
          (click)="discardPendingChanges()"
          >Cancel status changes</ui-button
        >
      </footer>
    </div></ui-panel
  >
</ng-template>
</section>
<ng-template #unsupportedConfiguration><section class="status-editor" [attr.aria-labelledby]="headingId"><h4 [id]="headingId" data-status-heading tabindex="-1">{{ title }}</h4><p role="status">{{ retainedIssue }}</p><pre>{{ retainedConfiguration | json }}</pre></section></ng-template>
